<script lang="ts" setup>
  import { ref } from 'vue';
  import { useHttpClient, useRoute } from '@othercommerce/foundation';
  import { useCustomerOrderAbility, useCustomerAccepted } from '@storefront/storefront';
  import type { ComparatorProductVariantResource } from '@storefront/resources';

  interface Props {
    variant: ComparatorProductVariantResource,
  }

  const props = defineProps<Props>();
  const route = useRoute();
  const canCustomerOrder = useCustomerOrderAbility();
  const isCustomerAccepted = useCustomerAccepted();
  const successModalVisible = ref(false);
  const http = useHttpClient();

  function onProductAdded() {
    successModalVisible.value = true;
  }

  function destroy() {
    http.delete(route('web.comparator.destroy', { 'variantId': props.variant.variantId }));
  }
</script>
<template>
  <div class="comparator-product">
    <SfButton :aria-label="$t('SHOP.COMPARATOR.ARIA.REMOVE_PRODUCT')" class="comparator-product__delete" danger rounded square transparent @click="destroy">
      <template #after>
        <SfIconClose aria-hidden="true" />
      </template>
    </SfButton>
    <RouterLink :href="$route('web.product.show', { slug: variant.slug })" class="comparator-product__link">
      <div class="comparator-product__media">
        <div class="comparator-product__image">
          <MediaPicture :photo="variant.photo" sizes="240x240 2x, 120x120" />
        </div>
        <SfHeading class="comparator-product__title" type="h2">
          {{ variant.title }}
        </SfHeading>
      </div>
    </RouterLink>
    <div v-if="canCustomerOrder">
      <div class="comparator-product__price">
        <SfProductPrice :price="variant" />
        <SfWishlistButton :variant="variant.variantId" :variantModel="variant" />
      </div>
      <SfProductOmnibus :price="variant" />
      <SfCartStoreButton :variant="variant" class="comparator-product__add-to-cart" @added="onProductAdded">
        <template v-if="variant" #after>
          <SfIconCartAdd aria-hidden="true" />
        </template>
      </SfCartStoreButton>
    </div>
    <div v-else>
      <div class="comparator-product__price__guest">
        <RouterLink v-if="isCustomerAccepted === undefined" :href="$route('web.account.session.create')" rel="nofollow">
          {{ $t('SHOP.PRICE.MESSAGE.SIGN_IN_TO_SEE_PRICES') }}
        </RouterLink>
        <span v-else>
          {{ $t('SHOP.PRICE.MESSAGE.ACCOUNT_NOT_ACTIVE') }}
        </span>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
  @use "@storefront/styles/mixins/media";

  .comparator-product {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    gap: 0.5rem;

    &__delete.btn {
      position: absolute;
      top: 5px;
      right: 5px;

      svg {
        font-size: 1rem;
      }
    }

    &__add-to-cart .cart-store-button__button {
      flex-direction: column;
      gap: 0.5rem;
      margin-top: 1rem;
    }

    &__link {
      font-size: 1rem;
      font-weight: var(--font-weight-regular);
    }

    &__media {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 100%;
    }

    &__image {
      display: grid;
      height: 90px;
      grid-template-rows: 1fr;
      transition: all 300ms ease-in-out;

      picture {
        overflow: hidden;

        img {
          height: 100%;
        }
      }
    }

    &__title {
      font-size: 0.875rem;
      font-weight: var(--font-weight-semi);
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 4;
      overflow: hidden;
    }

    &__price {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin: 0;

      &__guest {
        color: var(--secondary-color);
        font-size: 0.75rem;
      }
    }

    &.scroll {
      .comparator-product__media .comparator-product__image {
        height: 0;
        grid-template-rows: 0fr;
      }

      .comparator-product__delete {
        display: none;
      }
    }
  }
</style>
